/* eslint-disable no-console */
import { useState, useCallback, useRef, useEffect } from 'react'

import debounce from 'lodash.debounce'

import axios from 'axios'

import i18n from 'Pods/Shared/I18nHome'

import { sendExperimentEvent } from 'Utils/Experiments'
import { LessThanBreakpoint } from 'Utils/GetBreakpoint'
import { buildLocalizedPath } from 'Utils/getLocalizedUrl'

export default function useSearch({ placeholder = '', locale = 'en', printReady = false, aiMode = false } = {}) {
  const [suggestions, setSuggestions] = useState()
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [searchedValue, setSearchedValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [isTabletOrLess, setIsTabletOrLess] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const searchRef = useRef(null)
  const currentRequest = useRef(null)
  const isRequestPending = useRef(false)
  const requestCounter = useRef(0)

  const isOnClientSide = typeof window !== 'undefined'

  function handleResize() {
    setIsTabletOrLess(LessThanBreakpoint('md'))
    setIsMobile(LessThanBreakpoint('sm'))
  }

  function handleAppKeywordClick(suggestedKeyword) {
    if (suggestedKeyword && isOnClientSide && window.searchTracker) {
      window.searchTracker({
        searchTerm: suggestedKeyword,
        type: 'model',
      })
    }

    if (isOnClientSide) {
      window.location.href = buildLocalizedPath(
        printReady
          ? `/search?keywords=${suggestedKeyword}&print_ready=1`
          : `/search?keywords=${suggestedKeyword}`
      )
    }
  }

  // Cleanup function to cancel any pending requests
  useEffect(() => {
    return () => {
      if (currentRequest.current) {
        currentRequest.current.cancel()
      }
      isRequestPending.current = false
    }
  }, [])

  // handleSearchClick sets `submitting` before a full-page navigation. When the user returns via the
  // browser back/forward cache the page is restored with the JS heap frozen, leaving the loader stuck.
  // `pageshow` fires on bfcache restore (event.persisted === true); reset the spinner there.
  useEffect(() => {
    if (!isOnClientSide) return undefined

    const handlePageShow = (event) => {
      if (event.persisted) {
        setSubmitting(false)
      }
    }

    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  async function handleSuggestions(inputValue) {
    try {
      if (isRequestPending.current) {
        return { keywords: [], searchKeyword: inputValue }
      }

      isRequestPending.current = true
      requestCounter.current += 1
      const currentRequestId = requestCounter.current

      // Cancel previous request if it exists
      if (currentRequest.current) {
        currentRequest.current.cancel()
      }

      // Create a new cancel token
      const cancelToken = axios.CancelToken.source()
      currentRequest.current = cancelToken

      const response = await axios.get('/api/internal/items/live_search.json', {
        params: { query: inputValue },
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
        cancelToken: cancelToken.token,
        timeout: 3000,
      })

      if (currentRequestId !== requestCounter.current) {
        return { keywords: [], searchKeyword: inputValue }
      }

      const suggestionData = Array.isArray(response.data?.data)
        ? response.data.data
        : response.data

      if (Array.isArray(suggestionData)) {
        const keywordSuggestions = suggestionData.map(([suggestedKeyword, itemData]) => ({
          suggestedKeyword,
          thumbnailUrl: itemData.thumbnailUrl,
        }))

        return {
          keywords: [...keywordSuggestions],
          searchKeyword: inputValue,
          isSearchSuggestionList: response.data?.new_search_suggestion_list,
        }
      }

      return {
        ...suggestionData,
        searchKeyword: inputValue,
        isSearchSuggestionList: response.data?.new_search_suggestion_list,
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('Error fetching suggestions:', error)
      }
      return { keywords: [], searchKeyword: inputValue }
    } finally {
      isRequestPending.current = false
    }
  }

  const clearSuggestions = () => {
    setSuggestions(undefined)
    setShowSuggestions(false)
  }

  const handleSearchClick = () => {
    setSubmitting(true)
    const currentSearchValue = searchedValue.toLowerCase()

    // Cancel any pending requests and debounced calls
    if (currentRequest.current) {
      currentRequest.current.cancel()
    }
    debouncedChange.cancel()

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        handleAppKeywordClick(currentSearchValue)
      })
    })

    clearSuggestions()
  }

  const onKeywordClick = ({ suggestedKeyword, callback }) => {
    handleAppKeywordClick(suggestedKeyword)
    if (callback) callback()
    return null
  }

  const debouncedChange = useCallback(
    debounce(async (value) => {
      // AI mode drives its dropdown from grouped_live_search (useGroupedLiveSearch); the
      // live_search.json suggestions are never rendered here, so skip the redundant request.
      if (aiMode) return

      if (!value || !value.trim()) {
        setSuggestions(undefined)
        setShowSuggestions(false)
        return
      }

      const searchResults = await handleSuggestions(value)
      setSuggestions(searchResults)
      sendExperimentEvent('autocomplete_search_suggestion', searchResults?.autocomplete_search_suggestion)
      return setShowSuggestions(true)
    }, 300),
    [aiMode]
  )

  const debouncedResize = useCallback(debounce(handleResize, 300), [])

  async function handleChange(e) {
    e.preventDefault()
    e.stopPropagation()

    const { value } = e.target
    setSearchedValue(value)

    if (value.length > 0) {
      debouncedChange(value)
    } else {
      debouncedChange.cancel()
      setSuggestions(undefined)
      setShowSuggestions(false)
    }
  }

  function handleClick(e) {
    const isSuggestionsClicked = searchRef.current && searchRef.current.contains(e.target)
    if (!isSuggestionsClicked) {
      setShowSuggestions(false)
      setIsFocused(false)
    }
  }

  const handleFocus = () => setIsFocused(true)

  const handleKeyDown = (e) => {
    // Enter is handled by the form's onSubmit (the search button is type=submit), so it is not
    // intercepted here - duplicating it fired handleSearchClick twice (double navigation + tracking).
    if (e.key === 'Escape') {
      setShowSuggestions(false)
      // Clearing focus also closes the AI search dropdown, which is gated on isFocused.
      setIsFocused(false)
      debouncedChange.cancel()
    }
  }

  const handleKeywordSuggestionClick = ({ suggestedKeyword }) => {
    onKeywordClick({ suggestedKeyword })
    setSearchedValue('')
  }

  const handleFilterClick = (filterId) => {
    setSearchedValue(filterId)
    handleAppKeywordClick(filterId)
  }

  let actualPlaceholder = placeholder

  const hasProvidedPlaceholder = Boolean(actualPlaceholder)
    && !actualPlaceholder.includes('<')
    && !actualPlaceholder.includes('translation_missing')

  if (!hasProvidedPlaceholder) {
    actualPlaceholder = i18n.t(
      `search.placeholder.${isTabletOrLess ? 'mobile' : 'desktop'}`,
      { ns: 'new-home', lng: locale }
    )
  } else if (isMobile) {
    // Below the `sm` breakpoint (< 576px) the search bar narrows and the AI-mode controls consume
    // horizontal space, so the long provided placeholder gets cropped. Swap it for a short "Search"
    // label. `isMobile` is false at SSR (set client-side on resize), so the server renders the full
    // text; the swap only refines placeholder text and never changes the input height (no CLS).
    actualPlaceholder = i18n.t('search.placeholder.short', { ns: 'new-home', lng: locale })
  }

  useEffect(() => {
    if (isOnClientSide) {
      const queryString = window.location.search
      const urlParams = new URLSearchParams(queryString)
      const keywords = urlParams.get('keywords')
      if (keywords) {
        setSearchedValue(keywords)
      }
    }
  }, [])

  useEffect(() => {
    if (isOnClientSide) {
      window.addEventListener('click', handleClick)
      return () => window.removeEventListener('click', handleClick)
    }
    return undefined
  }, [])

  useEffect(() => {
    if (isOnClientSide) {
      handleResize() // Initial call
      window.addEventListener('resize', debouncedResize)
      return () => window.removeEventListener('resize', debouncedResize)
    }
    return undefined
  }, [debouncedResize])

  return {
    searchRef,
    searchedValue,
    submitting,
    suggestions,
    showSuggestions,
    isFocused,
    actualPlaceholder,
    handleChange,
    handleKeyDown,
    handleFocus,
    handleSearchClick,
    handleKeywordSuggestionClick,
    handleFilterClick,
    clearSuggestions,
    onKeywordClick,
  }
}
